import {
  useMutation,
  useQueryClient,
  UseMutationOptions,
} from "@tanstack/react-query";
import { useCallback } from "react";

/**
 * Standard mutation configurations
 */
export const MUTATION_CONFIG = {
  // Standard retry for mutations (usually don't retry mutations)
  retry: false,

  // Standard timeout for mutations
  timeout: 30000, // 30 seconds
} as const;

/**
 * Enhanced mutation hook with standardized error handling and optimistic updates
 */
export const useStandardMutation = <
  TData = unknown,
  TError = Error,
  TVariables = void,
  TContext = unknown
>(
  mutationFn: (variables: TVariables) => Promise<TData>,
  options?: {
    // Query keys to invalidate on success
    invalidateKeys?: string[][];

    // Optimistic update configuration
    optimisticUpdate?: {
      queryKey: string[];
      updater: (old: any, variables: TVariables) => any;
    };

    // Success/error callbacks
    onSuccess?: (data: TData, variables: TVariables, context: TContext) => void;
    onError?: (error: TError, variables: TVariables, context: TContext) => void;

    // Custom mutation options
    mutationOptions?: Omit<
      UseMutationOptions<TData, TError, TVariables, TContext>,
      "mutationFn"
    >;
  }
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn,
    retry: MUTATION_CONFIG.retry,

    onMutate: async (variables: TVariables) => {
      // Handle optimistic updates
      if (options?.optimisticUpdate) {
        const { queryKey, updater } = options.optimisticUpdate;

        // Cancel any outgoing refetches
        await queryClient.cancelQueries({ queryKey });

        // Snapshot the previous value
        const previousData = queryClient.getQueryData(queryKey);

        // Optimistically update to the new value
        queryClient.setQueryData(queryKey, (old: any) =>
          updater(old, variables)
        );

        // Return a context object with the snapshotted value
        return { previousData, queryKey };
      }

      return options?.mutationOptions?.onMutate?.(variables);
    },

    onError: (error: TError, variables: TVariables, context: any) => {
      // Rollback optimistic update on error
      if (context?.previousData && context?.queryKey) {
        queryClient.setQueryData(context.queryKey, context.previousData);
      }

      options?.onError?.(error, variables, context);
      options?.mutationOptions?.onError?.(error, variables, context);
    },

    onSuccess: (data: TData, variables: TVariables, context: TContext) => {
      // Invalidate specified queries on success
      if (options?.invalidateKeys) {
        options.invalidateKeys.forEach((queryKey) => {
          queryClient.invalidateQueries({ queryKey });
        });
      }

      options?.onSuccess?.(data, variables, context);
      options?.mutationOptions?.onSuccess?.(data, variables, context);
    },

    onSettled: (data, error, variables, context) => {
      options?.mutationOptions?.onSettled?.(data, error, variables, context);
    },

    ...options?.mutationOptions,
  });
};

/**
 * Hook for mutations that update user profile data
 */
export const useProfileMutation = <TData = unknown, TVariables = void>(
  mutationFn: (variables: TVariables) => Promise<TData>,
  options?: {
    onSuccess?: (data: TData, variables: TVariables) => void;
    onError?: (error: Error, variables: TVariables) => void;
    optimisticUpdate?: (old: any, variables: TVariables) => any;
  }
) => {
  return useStandardMutation(mutationFn, {
    invalidateKeys: [["profile"]],
    optimisticUpdate: options?.optimisticUpdate
      ? {
          queryKey: ["profile"],
          updater: options.optimisticUpdate,
        }
      : undefined,
    onSuccess: options?.onSuccess,
    onError: options?.onError,
  });
};

/**
 * Hook for mutations that update positions data
 */
export const usePositionsMutation = <TData = unknown, TVariables = void>(
  mutationFn: (variables: TVariables) => Promise<TData>,
  options?: {
    onSuccess?: (data: TData, variables: TVariables) => void;
    onError?: (error: Error, variables: TVariables) => void;
    optimisticUpdate?: (old: any, variables: TVariables) => any;
  }
) => {
  return useStandardMutation(mutationFn, {
    invalidateKeys: [["positions"]],
    optimisticUpdate: options?.optimisticUpdate
      ? {
          queryKey: ["positions"],
          updater: options.optimisticUpdate,
        }
      : undefined,
    onSuccess: options?.onSuccess,
    onError: options?.onError,
  });
};

/**
 * Hook for mutations that update preferences
 */
export const usePreferencesMutation = <TData = unknown, TVariables = void>(
  mutationFn: (variables: TVariables) => Promise<TData>,
  options?: {
    onSuccess?: (data: TData, variables: TVariables) => void;
    onError?: (error: Error, variables: TVariables) => void;
    optimisticUpdate?: (old: any, variables: TVariables) => any;
  }
) => {
  return useStandardMutation(mutationFn, {
    invalidateKeys: [["preferences"]],
    optimisticUpdate: options?.optimisticUpdate
      ? {
          queryKey: ["preferences"],
          updater: options.optimisticUpdate,
        }
      : undefined,
    onSuccess: options?.onSuccess,
    onError: options?.onError,
  });
};

/**
 * Hook for mutations that affect multiple data types
 */
export const useMultiDataMutation = <TData = unknown, TVariables = void>(
  mutationFn: (variables: TVariables) => Promise<TData>,
  options: {
    invalidateKeys: string[][];
    onSuccess?: (data: TData, variables: TVariables) => void;
    onError?: (error: Error, variables: TVariables) => void;
  }
) => {
  return useStandardMutation(mutationFn, {
    invalidateKeys: options.invalidateKeys,
    onSuccess: options.onSuccess,
    onError: options.onError,
  });
};

/**
 * Custom hook for cache management utilities
 */
export const useMutationCache = () => {
  const queryClient = useQueryClient();

  const invalidateAll = useCallback(() => {
    return queryClient.invalidateQueries();
  }, [queryClient]);

  const invalidateUserData = useCallback(() => {
    return Promise.all([
      queryClient.invalidateQueries({ queryKey: ["profile"] }),
      queryClient.invalidateQueries({ queryKey: ["positions"] }),
      queryClient.invalidateQueries({ queryKey: ["transactions"] }),
      queryClient.invalidateQueries({ queryKey: ["preferences"] }),
    ]);
  }, [queryClient]);

  const clearCache = useCallback(() => {
    return queryClient.clear();
  }, [queryClient]);

  return {
    invalidateAll,
    invalidateUserData,
    clearCache,
    queryClient,
  };
};
